import React from 'react';
import { Label } from "flowbite-react";

/**
 * Props for the FormInput component
 * @property id - Unique identifier for the input
 * @property name - Form field name
 * @property label - Optional label text above input
 * @property type - Input type (text or textarea or password)
 * @property value - Current input value
 * @property defaultValue - Default value for uncontrolled input
 * @property placeholder - Placeholder text
 * @property onChange - Callback function when input changes
 * @property maxLength - Maximum length of input
 * @property className - Additional CSS classes
 * @property required - Whether the field is required
 * @property showAsterisk - Whether to show the required asterisk
 * @property counter - Whether to show character counter
 * @property disabled - Whether the input is disabled
 */
interface FormInputProps {
  id: string;
  name: string;
  label?: string | JSX.Element;
  type?: 'text' | 'textarea' | 'password';
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  maxLength?: number;
  className?: string;
  required?: boolean;
  showAsterisk?: boolean;
  counter?: boolean;
  disabled?: boolean;
}

/**
 * FormInput Component
 * 
 * A reusable input component that provides:
 * - Text input and textarea support
 * - Character counter
 * - Label with optional required indicator
 * - Dark mode support
 * - Customizable styles
 * - Maximum length control
 * - Disabled state support
 * - Placeholder text
 */
const FormInput: React.FC<FormInputProps> = ({
  id,
  name,
  label,
  type = 'text',
  value,
  defaultValue,
  placeholder,
  onChange,
  maxLength,
  className = '',
  required = false,
  showAsterisk = false,
  counter = false,
  disabled = false,
}) => {
  /**
   * Base input styles including:
   * - Full width
   * - Border and rounded corners
   * - Focus states
   * - Dark mode variants
   * - Proper text colors for both modes
   */
  const baseInputStyles = `
    block w-full p-2 rounded-lg
    border border-gray-300 
    focus:ring-blue-500 focus:border-blue-500 
    bg-gray-50 
    text-gray-900
    placeholder-gray-500
    dark:bg-slate-800 
    dark:border-gray-700 
    dark:placeholder-gray-400 
    dark:text-white
  `;
  
  /**
   * Renders the character counter if enabled
   * Only shows when counter prop is true and value exists
   */
  const renderCounter = () => {
    if (!counter || !value || typeof value !== 'string') return null;
    return (
      <span className="ml-auto text-[10px]">
        {`${value.length} / ${maxLength}`}
      </span>
    );
  };

  /**
   * Renders the label with optional required indicator and counter
   * Shows asterisk only when both required and showAsterisk are true
   */
  const renderLabel = () => {
    if (!label) return null;
    return (
      <Label htmlFor={id} className="flex">
        <span className="text-gray-900 dark:text-white">
          {label}
          {showAsterisk && required && '*'}
        </span>
        {renderCounter()}
      </Label>
    );
  };

  /**
   * Renders the appropriate input element based on type
   * Supports text input, textarea, and password input with consistent styling
   */
  const renderInput = () => {
    if (type === 'textarea') {
      return (
        <textarea
          id={id}
          name={name}
          onChange={onChange}
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          className={`${baseInputStyles} h-40 ${className}`}
          disabled={disabled}
        />
      );
    }

    return (
      <input
        type={type}
        id={id}
        name={name}
        onChange={onChange}
        value={value}
        defaultValue={defaultValue}
        placeholder={placeholder}
        maxLength={maxLength}
        className={`${baseInputStyles} ${className}`}
        disabled={disabled}
      />
    );
  };

  return (
    <div className="mb-4">
      {renderLabel()}
      <div className="mt-2">
        {renderInput()}
      </div>
    </div>
  );
};

export default FormInput; 